/**
 * Ride entity adapter: maps the abstract flow model onto the `rides` table.
 * Parties are driver / passenger. The request recipient (actorKey 'recipient')
 * is the ride post owner, passed in as ownerId by the engine.
 */

const partyOf = (row, userId) => (userId === row.driver_id ? 'driver' : 'passenger');
const otherParty = (p) => (p === 'driver' ? 'passenger' : 'driver');

const accusedId = (row) => {
  if (row.no_show_party === 'driver') return row.driver_id;
  if (row.no_show_party === 'passenger') return row.passenger_id;
  return null;
};

export const rideAdapter = {
  entity: 'ride',
  table: 'rides',

  actorKeys(row, userId, ownerId) {
    const keys = ['either'];
    if (userId === ownerId) keys.push('recipient');
    if (row.status === 'disputed_no_show' && userId === accusedId(row)) keys.push('accused');
    return keys;
  },

  abstract(row, { userId, travelDate } = {}) {
    const p = userId ? partyOf(row, userId) : null;
    const myArrival = p === 'driver' ? row.driver_arrival_time : p === 'passenger' ? row.passenger_arrival_time : null;
    const otherArrival = p === 'driver' ? row.passenger_arrival_time : p === 'passenger' ? row.driver_arrival_time : null;
    const myReady = p === 'driver' ? row.driver_ready_time : p === 'passenger' ? row.passenger_ready_time : null;
    const otherReady = p === 'driver' ? row.passenger_ready_time : p === 'passenger' ? row.driver_ready_time : null;
    const myPresence = p === 'driver' ? row.driver_present_confirmed_at : p === 'passenger' ? row.passenger_present_confirmed_at : null;
    const otherPresence = p === 'driver' ? row.passenger_present_confirmed_at : p === 'passenger' ? row.driver_present_confirmed_at : null;
    const proposedBy = row.meeting_time_proposal ? row.meeting_time_proposal.by : null;
    return {
      proposedBy,
      iProposed: proposedBy != null && proposedBy === userId,
      iArrived: Boolean(myArrival),
      otherArrived: Boolean(otherArrival),
      iPresenceConfirmed: Boolean(myPresence),
      otherPresenceConfirmed: Boolean(otherPresence),
      iConfirmed: Boolean(myReady),
      otherConfirmed: Boolean(otherReady),
      travelDate: travelDate || null,
      meetingTime: row.meeting_time || null,
      disputedAt: row.status === 'disputed_no_show' ? row.updated_at : null,
    };
  },

  contextFor(row, userId, { ownerId, travelDate } = {}) {
    return {
      actorKeys: this.actorKeys(row, userId, ownerId),
      userId,
      row: this.abstract(row, { userId, travelDate }),
    };
  },

  payloadFor(action, { userId, row, meetingTime, reason, now, geo }) {
    const p = partyOf(row, userId);
    switch (action) {
      case 'propose_time':
        return { meeting_time: meetingTime, meeting_time_proposal: { by: userId, time: meetingTime } };
      case 'agree_time':
        return { meeting_time: (row.meeting_time_proposal && row.meeting_time_proposal.time) || row.meeting_time };
      case 'mark_here': {
        // Advisory location proof stored per party (existing dormant columns).
        const out = p === 'driver' ? { driver_arrival_time: now } : { passenger_arrival_time: now };
        if (geo) out[p === 'driver' ? 'driver_location_proof' : 'passenger_location_proof'] = geo;
        return out;
      }
      case 'confirm_presence':
        return p === 'driver' ? { driver_present_confirmed_at: now } : { passenger_present_confirmed_at: now };
      case 'dispute_presence':
        return {
          no_show_party: otherParty(p),
          flow_dispute_record: { type: 'presence', disputed_by: userId, accused_party: otherParty(p), at: now },
        };
      case 'cancel_as_no_show':
        return { no_show_party: p, cancellation_reason: p === 'driver' ? 'driver_no_show' : 'passenger_no_show' };
      case 'confirm_handoff':
        return p === 'driver' ? { driver_ready_time: now } : { passenger_ready_time: now };
      case 'report_no_show':
        return { no_show_party: otherParty(p) };
      case 'cancel':
        return { cancellation_reason: reason || 'cancelled' };
      case 'dispute_no_show':
        return {
          cancellation_reason: 'no_show_disputed',
          flow_dispute_record: { type: 'no_show_appeal', disputed_by: userId, at: now },
        };
      case 'mark_done':
        return { completion_source: 'self_reported' };
      default:
        return {};
    }
  },

  // Rides are a single leg — never advance to another.
  nextLegOn() {
    return null;
  },
};
